import { getVacancyLifecycleStatus } from '../components/VacancyList';
import { formatClientFourDigitId } from './clientDisplay';
import { isClientUuid, isPortalArchivedVacancy } from './clientPortalGuards';

export type VacancyLifecycleStage = 'open' | 'doing' | 'gesloten';

export const VACANCY_LIFECYCLE_STAGE_OPTIONS: { id: VacancyLifecycleStage; label: string }[] = [
    { id: 'open', label: 'Open' },
    { id: 'doing', label: 'Doing' },
    { id: 'gesloten', label: 'Gesloten' },
];

/** Multi-select lifecycle (Open / Doing / Gesloten). */
export function vacancyMatchesLifecycleStages(
    v: any,
    stages: readonly VacancyLifecycleStage[],
    options?: { excludePortalArchived?: boolean }
): boolean {
    if (!stages.length) return false;
    if (options?.excludePortalArchived && isPortalArchivedVacancy(v)) return false;
    const computedStatus = getVacancyLifecycleStatus(v).toLowerCase() as VacancyLifecycleStage;
    return (stages as readonly string[]).includes(computedStatus);
}

export function countVacanciesForLifecycleStages(
    vacancies: readonly any[],
    stages: readonly VacancyLifecycleStage[],
    options?: { excludePortalArchived?: boolean }
): number {
    return vacancies.filter((v) => vacancyMatchesLifecycleStages(v, stages, options)).length;
}

export function filterVacanciesByStagesAndClient(
    vacancies: readonly any[],
    stages: readonly VacancyLifecycleStage[],
    clientScope: VacancyClientFilterScope,
    options?: { excludePortalArchived?: boolean }
): any[] {
    return filterVacanciesInScope(
        vacancies.filter((v) => vacancyMatchesLifecycleStages(v, stages, options)),
        clientScope
    );
}

/** Migreer opgeslagen `vacancyFilter` (ALL_* of uuid) naar multi-select state. */
export function parseLegacyVacancyFilterToLifecycleState(
    filter: string,
    isClientPortal: boolean
): { stages: VacancyLifecycleStage[]; selectedVacancyId: string } {
    const f = String(filter || '').trim();
    if (f && !f.startsWith('ALL_') && f !== 'ALL') {
        return {
            stages: isClientPortal ? ['open', 'doing', 'gesloten'] : ['open'],
            selectedVacancyId: f,
        };
    }
    if (isClientPortal && (f === 'ALL_OPEN' || f === '')) {
        return { stages: ['open', 'doing', 'gesloten'], selectedVacancyId: '' };
    }
    switch (f) {
        case 'ALL_GESLOTEN':
            return { stages: ['gesloten'], selectedVacancyId: '' };
        case 'ALL_DOING':
            return { stages: ['doing'], selectedVacancyId: '' };
        case 'ALL_OPEN_GESLOTEN':
            return { stages: ['open', 'gesloten'], selectedVacancyId: '' };
        case 'ALL_OPEN_DOING':
            return { stages: ['open', 'doing'], selectedVacancyId: '' };
        case 'ALL_OPEN_GESLOTEN_DOING':
            return { stages: ['open', 'doing', 'gesloten'], selectedVacancyId: '' };
        case 'ALL_PUBLISHED':
            return { stages: ['open'], selectedVacancyId: '' };
        case 'ALL_OPEN':
        default:
            return { stages: ['open'], selectedVacancyId: '' };
    }
}

/** Map multi-select stages naar KanbanBoard `vacancyId` (ALL_*). */
export function kanbanVacancyIdFromLifecycleStages(stages: readonly VacancyLifecycleStage[]): string {
    const o = stages.includes('open');
    const d = stages.includes('doing');
    const g = stages.includes('gesloten');
    if (o && d && g) return 'ALL_OPEN_GESLOTEN_DOING';
    if (o && g && !d) return 'ALL_OPEN_GESLOTEN';
    if (o && d && !g) return 'ALL_OPEN_DOING';
    if (g && !o && !d) return 'ALL_GESLOTEN';
    if (d && !o && !g) return 'ALL_DOING';
    if (o && !d && !g) return 'ALL_OPEN';
    if (d && g) return 'ALL_OPEN_GESLOTEN_DOING';
    return 'ALL_OPEN_GESLOTEN_DOING';
}

/** Vacature binnen gekozen pipeline/shortlist lifecycle-filter (ALL_OPEN, …). */
export function vacancyMatchesLifecycleFilter(
    v: any,
    vacancyFilter: string,
    options?: { excludePortalArchived?: boolean }
): boolean {
    if (!vacancyFilter) return true;
    if (options?.excludePortalArchived && isPortalArchivedVacancy(v)) return false;
    const computedStatus = getVacancyLifecycleStatus(v).toLowerCase();
    if (vacancyFilter === 'ALL_OPEN') return computedStatus === 'open';
    if (vacancyFilter === 'ALL_GESLOTEN') return computedStatus === 'gesloten';
    if (vacancyFilter === 'ALL_OPEN_GESLOTEN')
        return computedStatus === 'open' || computedStatus === 'gesloten';
    if (vacancyFilter === 'ALL_OPEN_GESLOTEN_DOING')
        return computedStatus === 'open' || computedStatus === 'gesloten' || computedStatus === 'doing';
    if (vacancyFilter === 'ALL_DOING') return computedStatus === 'doing';
    if (vacancyFilter === 'ALL_OPEN_DOING') return computedStatus === 'open' || computedStatus === 'doing';
    if (vacancyFilter === 'ALL_PUBLISHED') return v?.data?.publishedToSite === true;
    if (!vacancyFilter.startsWith('ALL_') && vacancyFilter !== 'ALL') return v?.id === vacancyFilter;
    return true;
}

/** Aantal vacatures binnen een globaal lifecycle-filter (ALL_OPEN, …). */
export function countVacanciesForLifecycleFilter(
    vacancies: readonly any[],
    vacancyFilter: string,
    options?: { excludePortalArchived?: boolean }
): number {
    return vacancies.filter((v) => vacancyMatchesLifecycleFilter(v, vacancyFilter, options)).length;
}

export function filterVacanciesByLifecycleAndClient(
    vacancies: readonly any[],
    vacancyFilter: string,
    clientScope: VacancyClientFilterScope,
    options?: { excludePortalArchived?: boolean }
): any[] {
    return filterVacanciesInScope(
        vacancies.filter((v) => vacancyMatchesLifecycleFilter(v, vacancyFilter, options)),
        clientScope
    );
}

/** Expliciete opdrachtgever-FK (geen contract-`client_id`). */
export function vacancyOpdrachtgeverId(v: any): string {
    if (!v) return '';
    return String(
        v.opdrachtgever_id ?? v?.data?.opdrachtgever_id ?? v?.opdrachtgever?.id ?? ''
    ).trim();
}

/**
 * Id voor opdrachtgever-filter: `opdrachtgever_id`, anders `client_id` wanneer de UI
 * de contractpartij als opdrachtgever toont (zelfde fallback als shortlist-tabel).
 */
export function effectiveOpdrachtgeverFilterId(v: any): string {
    if (!v) return '';
    const og = vacancyOpdrachtgeverId(v);
    if (isClientUuid(og)) return og;
    const cid = String(v.client_id ?? v?.data?.client_id ?? '').trim();
    if (isClientUuid(cid)) return cid;
    return '';
}

/** Werkplek / eindklant = `vacancies.eindklant_id`. */
export function vacancyEindklantId(v: any): string {
    if (!v) return '';
    return String(v.eindklant_id ?? v?.data?.eindklant_id ?? v?.eindklant?.id ?? '').trim();
}

export function vacancyMatchesOpdrachtgeverFilter(v: any, opdrachtgeverId: string): boolean {
    if (!opdrachtgeverId) return true;
    if (!v) return false;
    const og = effectiveOpdrachtgeverFilterId(v);
    return Boolean(og && og === String(opdrachtgeverId).trim());
}

export function vacancyMatchesEindklantFilter(v: any, eindklantId: string): boolean {
    if (!eindklantId) return true;
    if (!v) return false;
    const ek = vacancyEindklantId(v);
    return Boolean(ek && isClientUuid(ek) && ek === String(eindklantId).trim());
}

export type VacancyClientFilterScope = {
    opdrachtgeverId?: string;
    eindklantId?: string;
};

export function vacancyMatchesClientFilters(v: any, scope: VacancyClientFilterScope): boolean {
    if (!vacancyMatchesOpdrachtgeverFilter(v, scope.opdrachtgeverId || '')) return false;
    if (!vacancyMatchesEindklantFilter(v, scope.eindklantId || '')) return false;
    return true;
}

export function filterVacanciesInScope<T extends { id?: string }>(
    vacancies: readonly T[],
    scope: VacancyClientFilterScope
): T[] {
    if (!scope.opdrachtgeverId && !scope.eindklantId) return [...vacancies];
    return vacancies.filter((v) => vacancyMatchesClientFilters(v, scope));
}

/** Vacatures die in de huidige applicatielijst voorkomen (voor og/ek-filteropties). */
export function vacanciesFromApplications(
    apps: readonly any[],
    vacancyMap?: Record<string, any>,
    globalVacancies?: readonly any[],
    localVacancies?: readonly any[]
): any[] {
    const seen = new Set<string>();
    const out: any[] = [];
    const add = (v: any) => {
        const id = String(v?.id || '').trim();
        if (!id || seen.has(id)) return;
        seen.add(id);
        out.push(v);
    };
    for (const app of apps) {
        add(resolveVacancyForApplication(app, vacancyMap, globalVacancies, localVacancies));
    }
    return out;
}

function isThinVacancyEmbed(v: any): boolean {
    if (!v || typeof v !== 'object') return true;
    if (v.client_id || v.opdrachtgever_id || v.eindklant_id) return false;
    if (v.opdrachtgever || v.eindklant || v.clients || v.client) return false;
    const d = v.data;
    if (d && typeof d === 'object' && (d.opdrachtgever_id || d.eindklant_id || d.client_id)) return false;
    return !v.id;
}

export function resolveVacancyForApplication(
    app: any,
    vacancyMap?: Record<string, any>,
    globalVacancies?: readonly any[],
    localVacancies?: readonly any[]
): any | null {
    const vid = String(app?.vacancy_id || '').trim();
    if (vid && vacancyMap?.[vid]) return vacancyMap[vid];
    if (vid) {
        const fromGlobal = globalVacancies?.find((v: any) => v.id === vid);
        if (fromGlobal) return fromGlobal;
        const fromLocal = localVacancies?.find((v: any) => v.id === vid);
        if (fromLocal) return fromLocal;
    }
    const embed = app?.vacancies ?? app?.vacancy;
    if (embed) {
        const v = Array.isArray(embed) ? embed[0] : embed;
        if (!isThinVacancyEmbed(v)) return v;
    }
    return null;
}

export function applicationMatchesClientFilters(
    app: any,
    scope: VacancyClientFilterScope,
    vacancyMap?: Record<string, any>,
    globalVacancies?: readonly any[],
    localVacancies?: readonly any[]
): boolean {
    const vac = resolveVacancyForApplication(app, vacancyMap, globalVacancies, localVacancies);
    if (!vac) return !scope.opdrachtgeverId && !scope.eindklantId;
    return vacancyMatchesClientFilters(vac, scope);
}

export type ClientFilterOption = {
    id: string;
    companyName: string;
    companyNumber: string;
    /** Ruwe `bullhorn_id` voor zoeken (341 én 0341). */
    bullhornRaw?: string;
    count: number;
};

/** Label in filterknop/lijst: «0341 AIM». */
export function formatClientFilterOptionLabel(opt: ClientFilterOption): string {
    const num = String(opt.companyNumber || '').trim();
    if (num) return `${num} ${opt.companyName}`;
    return opt.companyName;
}

export function clientFilterOptionSearchHaystack(opt: ClientFilterOption): string {
    return `${opt.companyName} ${opt.companyNumber} ${opt.bullhornRaw || ''} ${opt.id}`.trim();
}

export type ClientFilterMapEntry = {
    name?: string;
    logo?: string;
    client_number?: string | number | null;
    bullhorn_id?: string | number | null;
    invoice_kvk?: string;
    data?: unknown;
};

/** Contractpartij-embed: Supabase `clients`, App-state `client`. */
export function vacancyContractClientEmbed(v: any): any | null {
    if (!v) return null;
    return v.clients ?? v.client ?? null;
}

function embeddedClientOnVacancy(v: any, role: 'opdrachtgever' | 'eindklant'): any | null {
    if (!v) return null;
    if (role === 'opdrachtgever') {
        if (v.opdrachtgever?.name) return v.opdrachtgever;
        const effId = effectiveOpdrachtgeverFilterId(v);
        const contract = vacancyContractClientEmbed(v);
        if (effId && contract?.name && String(v.client_id || '').trim() === effId) return contract;
        return null;
    }
    if (v.eindklant?.name) return v.eindklant;
    return null;
}

/** Zelfde fallback-keten als shortlist-tabel (zonder clientMap-async). */
export function resolveOpdrachtgeverDisplayName(
    vac: any,
    clientMap?: Record<string, ClientFilterMapEntry>,
    app?: any
): string {
    if (!vac) return String(app?.source_client?.name || '').trim() || 'Onbekend';
    const opId = effectiveOpdrachtgeverFilterId(vac);
    if (opId && clientMap?.[opId]?.name) return clientMap[opId].name;
    const embed = embeddedClientOnVacancy(vac, 'opdrachtgever');
    if (embed?.name) return String(embed.name).trim();
    const text = vac?.data?.opdrachtgever || vac?.data?.clientName;
    if (typeof text === 'string' && text.trim()) return text.trim();
    const contract = vacancyContractClientEmbed(vac);
    if (contract?.name) return String(contract.name).trim();
    if (app?.source_client?.name) return String(app.source_client.name).trim();
    return 'Onbekend';
}

export function resolveEindklantDisplayName(
    vac: any,
    clientMap?: Record<string, ClientFilterMapEntry>
): string {
    if (!vac) return '-';
    const ekId = vacancyEindklantId(vac);
    if (ekId && clientMap?.[ekId]?.name) return clientMap[ekId].name;
    if (vac?.eindklant?.name) return String(vac.eindklant.name).trim();
    const text = vac?.data?.eindklant || vac?.data?.werkplek;
    if (typeof text === 'string' && text.trim()) return text.trim();
    return '-';
}

/** Naam + nummers voor filterlabels (clientMap, vacancy-embed, data-tekst). */
export function resolveClientFilterMeta(
    id: string,
    clientMap: Record<string, ClientFilterMapEntry>,
    vacancies: readonly any[],
    role: 'opdrachtgever' | 'eindklant'
): ClientFilterMapEntry {
    const fromMap = clientMap[id];
    if (fromMap?.name) return fromMap;

    for (const v of vacancies) {
        const vid = role === 'opdrachtgever' ? effectiveOpdrachtgeverFilterId(v) : vacancyEindklantId(v);
        if (vid !== id) continue;
        const embed = embeddedClientOnVacancy(v, role);
        if (embed?.name) {
            return {
                name: String(embed.name).trim(),
                logo: embed.logo_url,
                bullhorn_id: embed.bullhorn_id ?? null,
                invoice_kvk: embed.invoice_kvk,
                data: embed.data,
            };
        }
        if (role === 'opdrachtgever') {
            const text = v.data?.opdrachtgever || v.data?.clientName;
            if (typeof text === 'string' && text.trim()) return { name: text.trim() };
        } else {
            const text = v.data?.werkplek || v.data?.clientName;
            if (typeof text === 'string' && text.trim()) return { name: text.trim() };
        }
    }
    return fromMap || {};
}

function companyNumberForFilterClient(
    id: string,
    clientMap: Record<string, ClientFilterMapEntry>,
    vacancies: readonly any[],
    role: 'opdrachtgever' | 'eindklant'
): string {
    const meta = resolveClientFilterMeta(id, clientMap, vacancies, role);
    const n = formatClientFourDigitId(meta);
    if (n) return n;
    return '';
}

/** Vul clientMap vanuit vacancy-embeds (liveVacancies) vóór aparte clients-query. */
export function seedClientMapFromVacancyEmbeds(
    vacancies: readonly any[],
    base: Record<string, ClientFilterMapEntry> = {}
): Record<string, ClientFilterMapEntry> {
    const merged = { ...base };
    for (const v of vacancies) {
        for (const role of ['opdrachtgever', 'eindklant'] as const) {
            const id = role === 'opdrachtgever' ? effectiveOpdrachtgeverFilterId(v) : vacancyEindklantId(v);
            if (!isClientUuid(id) || merged[id]?.name) continue;
            const embed = embeddedClientOnVacancy(v, role);
            if (!embed?.name) continue;
            merged[id] = {
                name: String(embed.name).trim(),
                logo: embed.logo_url,
                bullhorn_id: embed.bullhorn_id ?? null,
                invoice_kvk: embed.invoice_kvk,
                data: embed.data,
            };
        }
    }
    return merged;
}

export function buildOpdrachtgeverFilterOptions(
    vacancies: readonly any[],
    clientMap: Record<string, ClientFilterMapEntry>,
    apps?: readonly any[],
    vacancyMap?: Record<string, any>,
    globalVacancies?: readonly any[],
    localVacancies?: readonly any[]
): ClientFilterOption[] {
    const counts = new Map<string, number>();
    const sourceVacancies =
        vacancies.length > 0
            ? vacancies
            : vacanciesFromApplications(apps || [], vacancyMap, globalVacancies, localVacancies);

    if (apps?.length) {
        for (const app of apps) {
            const vac = resolveVacancyForApplication(app, vacancyMap, globalVacancies, localVacancies);
            const id = effectiveOpdrachtgeverFilterId(vac);
            if (!isClientUuid(id)) continue;
            counts.set(id, (counts.get(id) || 0) + 1);
        }
    } else {
        for (const v of sourceVacancies) {
            const id = effectiveOpdrachtgeverFilterId(v);
            if (!isClientUuid(id)) continue;
            counts.set(id, (counts.get(id) || 0) + 1);
        }
    }

    return Array.from(counts.entries())
        .map(([id, count]) => {
            const meta = resolveClientFilterMeta(id, clientMap, sourceVacancies, 'opdrachtgever');
            const bhRaw =
                meta.bullhorn_id != null && String(meta.bullhorn_id).trim() !== ''
                    ? String(meta.bullhorn_id).trim()
                    : '';
            const companyName = String(meta.name || '').trim() || 'Onbekend';
            return {
                id,
                companyName,
                companyNumber: companyNumberForFilterClient(id, clientMap, sourceVacancies, 'opdrachtgever'),
                bullhornRaw: bhRaw,
                count,
            };
        })
        .filter((opt) => opt.companyName !== 'Onbekend' || Boolean(opt.companyNumber))
        .sort((a, b) => a.companyName.localeCompare(b.companyName));
}

export function buildEindklantFilterOptions(
    vacancies: readonly any[],
    clientMap: Record<string, ClientFilterMapEntry>,
    apps?: readonly any[],
    vacancyMap?: Record<string, any>,
    globalVacancies?: readonly any[],
    localVacancies?: readonly any[]
): ClientFilterOption[] {
    const counts = new Map<string, number>();
    const sourceVacancies =
        vacancies.length > 0
            ? vacancies
            : vacanciesFromApplications(apps || [], vacancyMap, globalVacancies, localVacancies);

    if (apps?.length) {
        for (const app of apps) {
            const vac = resolveVacancyForApplication(app, vacancyMap, globalVacancies, localVacancies);
            const id = vacancyEindklantId(vac);
            if (!isClientUuid(id)) continue;
            counts.set(id, (counts.get(id) || 0) + 1);
        }
    } else {
        for (const v of sourceVacancies) {
            const id = vacancyEindklantId(v);
            if (!isClientUuid(id)) continue;
            counts.set(id, (counts.get(id) || 0) + 1);
        }
    }

    return Array.from(counts.entries())
        .map(([id, count]) => {
            const meta = resolveClientFilterMeta(id, clientMap, sourceVacancies, 'eindklant');
            const bhRaw =
                meta.bullhorn_id != null && String(meta.bullhorn_id).trim() !== ''
                    ? String(meta.bullhorn_id).trim()
                    : '';
            const companyName = String(meta.name || '').trim() || 'Onbekend';
            return {
                id,
                companyName,
                companyNumber: companyNumberForFilterClient(id, clientMap, sourceVacancies, 'eindklant'),
                bullhornRaw: bhRaw,
                count,
            };
        })
        .filter((opt) => opt.companyName !== 'Onbekend' || Boolean(opt.companyNumber))
        .sort((a, b) => a.companyName.localeCompare(b.companyName));
}
